import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Upload, Landmark, CheckCircle2, AlertTriangle, RotateCw, ArrowRight } from "lucide-react";
import { fmtINR } from "@/lib/format";

/**
 * Fixed Assets Register Wizard
 * ────────────────────────────
 * Same 3-step preview → commit pattern as Opening Balances. Supports dual
 * depreciation (Sch II SLM/WDV + IT Act WDV block). Sample template ships
 * with 3 example rows so accountants can see the exact column contract.
 */
const inr = (n) => fmtINR(n || 0);

export default function FixedAssetsWizard() {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [done, setDone]       = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/fixed-assets/register/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = "fixed_assets_register_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded — 3 sample rows included");
    } catch { toast.error("Download failed"); }
  };

  const uploadPreview = async () => {
    if (!file) return toast.error("Pick a CSV file first");
    setBusy(true); setPreview(null); setDone(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/fixed-assets/register/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      if (r.data.errors.length)
        toast.warning(`${r.data.errors.length} row(s) have issues — review before import`);
      else
        toast.success(`Preview ready — ${r.data.row_count} assets found`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };

  const commit = async () => {
    if (!preview) return;
    const rows = preview.rows.filter(r => r.issues.length === 0);
    if (!rows.length) return toast.error("No clean rows to import");
    setBusy(true);
    try {
      const r = await api.post("/fixed-assets/register/import", { rows });
      setDone(r.data);
      toast.success(`Imported ${r.data.imported} assets · ${r.data.skipped} skipped`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="fa-register-wizard">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Fixed Assets Register</div>
        <div className="text-sm text-muted-foreground mt-1">
          Migrate your existing fixed-assets register (Sch II + IT Act) so day-one depreciation runs correctly from month one.
        </div>
      </div>

      {/* Step 1 */}
      <Card className="p-4 rounded-sm border">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-accent/15 text-accent flex items-center justify-center font-mono text-xs font-bold">1</div>
            <div>
              <div className="font-semibold text-sm">Download the template</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                16 columns — cost, purchase date, Sch II useful-life + method, IT block rate, and the accumulated depreciation you've already booked.
              </div>
            </div>
          </div>
          <Button onClick={downloadTemplate} variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="fa-download-template">
            <Download className="w-3.5 h-3.5" /> Register CSV
          </Button>
        </div>
      </Card>

      {/* Step 2 */}
      <Card className="p-4 rounded-sm border">
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-full bg-accent/15 text-accent flex items-center justify-center font-mono text-xs font-bold">2</div>
          <div className="flex-1">
            <div className="font-semibold text-sm">Upload your filled register</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 mb-3">
              We validate every row (cost &gt; 0, accumulated dep ≤ cost) and show book + tax WDV totals before you commit.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input type="file" accept=".csv,text/csv"
                       onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setDone(null); }}
                       className="rounded-sm max-w-sm h-9" data-testid="fa-file-input" />
              <Button onClick={uploadPreview} disabled={busy || !file} size="sm"
                        className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="fa-preview-btn">
                {busy ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Landmark className="w-3.5 h-3.5" />} Preview
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Preview */}
      {preview && (
        <Card className="rounded-sm border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Preview</div>
              <div className="font-semibold text-sm mt-0.5">{preview.row_count} rows · {preview.errors.length} with issues</div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <Tile label="Gross Block"    value={inr(preview.totals.gross_block)} />
              <Tile label="Sch II Acc Dep" value={inr(preview.totals.book_acc_dep)} />
              <Tile label="IT Acc Dep"     value={inr(preview.totals.tax_acc_dep)} />
              <Tile label="Book WDV"       value={inr(preview.totals.book_wdv)} tone="emerald" />
              <Tile label="Tax WDV"        value={inr(preview.totals.tax_wdv)} tone="blue" />
            </div>
          </div>

          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs min-w-[1100px]">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40 sticky top-0">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Code</th>
                  <th className="p-2 text-left">Asset</th>
                  <th className="p-2 text-left">Category</th>
                  <th className="p-2 text-left">Put to use</th>
                  <th className="p-2 text-right">Cost</th>
                  <th className="p-2 text-right">Sch II Dep</th>
                  <th className="p-2 text-right">Book WDV</th>
                  <th className="p-2 text-right">IT WDV</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(r => {
                  const bad = r.issues.length > 0;
                  return (
                    <tr key={r.line} className={`border-t ${bad ? "bg-red-500/5" : ""}`} data-testid={`fa-row-${r.line}`}>
                      <td className="p-2 text-muted-foreground">{r.line}</td>
                      <td className="p-2 font-mono">{r.asset_code}</td>
                      <td className="p-2 font-medium">{r.name || "—"}</td>
                      <td className="p-2">{r.category}</td>
                      <td className="p-2 font-mono text-[10px]">{r.put_to_use_date || "—"}</td>
                      <td className="p-2 text-right font-mono">{inr(r.cost)}</td>
                      <td className="p-2 text-right font-mono">{inr(r.book_accumulated_dep)}</td>
                      <td className="p-2 text-right font-mono">{inr(r.book_wdv)}</td>
                      <td className="p-2 text-right font-mono">{inr(r.tax_wdv)}</td>
                      <td className="p-2 text-[10px]">
                        {bad ? <span className="text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {r.issues.join(" · ")}</span>
                             : <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ready</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-accent/15 text-accent flex items-center justify-center font-mono text-xs font-bold">3</div>
              <div>
                <div className="font-semibold text-sm">Commit register</div>
                <div className="text-[11px] text-muted-foreground">
                  Only clean rows will be imported. Depreciation will start from month one using the values above.
                </div>
              </div>
            </div>
            <Button onClick={commit} disabled={busy || done !== null} size="sm"
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid="fa-commit-btn">
              <Upload className="w-3.5 h-3.5" /> {done ? "Imported" : "Import"}
            </Button>
          </div>

          {done && (
            <div className="p-3 border-t bg-emerald-500/5 text-sm text-emerald-700 flex items-center gap-2" data-testid="fa-done">
              <CheckCircle2 className="w-4 h-4" /> Imported <b>{done.imported}</b> assets · skipped <b>{done.skipped}</b>. Head to <a href="/app/fixed-assets" className="underline">Fixed Assets</a> to run this month's depreciation.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Tile({ label, value, tone }) {
  const toneCls = {
    emerald: "bg-emerald-500/10 text-emerald-700",
    blue:    "bg-blue-500/10 text-blue-700",
  }[tone] || "";
  return (
    <div className={`px-3 py-1.5 rounded-sm ${toneCls}`}>
      <div className="text-[9px] font-mono uppercase text-muted-foreground">{label}</div>
      <div className="font-mono font-semibold text-sm">{value}</div>
    </div>
  );
}
